/**
 * AuthGuard — wraps protected routes. Behavior:
 *   isLoading        → centered spinner (initial /me fetch)
 *   user = null      → redirect to /login (no Optum identity at all)
 *   user not staff   → redirect to /login (signed in to the realm but not in
 *                      platform_staff). LoginPage surfaces the rejection.
 *   user is staff    → render the wrapped feature.
 *
 * `returnTo` is stashed in location.state so LoginPage can bounce back
 * after a successful sign-in. Internal-only routes are admin-web's
 * exclusive scope; client and federated users have no business here.
 */
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { Spinner } from '../ui/Spinner.js';
import { useAuth } from './AuthContext.js';

export interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { user, isInternal, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spinner size={24} />
      </div>
    );
  }

  if (!user || !isInternal) {
    return <Navigate to="/login" replace state={{ returnTo: location.pathname }} />;
  }

  return <>{children}</>;
}
